'use strict';

// BAP (Bitcoin Attestation Protocol) on-chain identity — derived from the SAME mnemonic as the
// rest of the vault, so a user's did:web and their on-chain BAP identity share one seed. Publishing
// a BAP ID record makes the identity verifiable directly on Bitcoin (via the AIP signature),
// independent of the web3keys.com DID resolver. Ported byte-for-byte from the SmartLedger wallet's
// lib/bap-identity.js so records are interoperable; uses bsv only (browser + server safe).

const { getBsv } = require('./lib');

// Buffer isn't a browser global; @smartledger/bsv ships it (deps.Buffer) in both Node and the
// browser bundle, so source it from there uniformly.
const Buf = () => getBsv().deps.Buffer;

// BAP canonical hardened derivation: 0x424150 = ASCII "BAP".
const BAP_ROOT_PATH = "m/424150'/0'/0'/0/0/0";
const BAP_CURRENT_PATH = "m/424150'/0'/0'/0/0/1";
const BAP_BITCOM_ADDRESS = '1BAPSuaPnfGnSBM3GLV9yhxUdYe4vGbdMT';
const AIP_BITCOM_ADDRESS = '15PciHG22SNLQJXMoSUaWVi7WSqc7hCfva';

// bapId = base58(ripemd160(sha256(utf8(rootAddress)))). Matches the wallet's computeBapId.
function computeBapId(rootAddress) {
  const bsv = getBsv();
  const h1 = bsv.crypto.Hash.sha256(Buf().from(rootAddress));
  const h2 = bsv.crypto.Hash.ripemd160(h1);
  return bsv.encoding.Base58(h2).toString();
}

function deriveBapKeys(hdPrivKey, currentPath) {
  const rootChild = hdPrivKey.deriveChild(BAP_ROOT_PATH);
  const currChild = hdPrivKey.deriveChild(currentPath || BAP_CURRENT_PATH);
  const rootAddress = rootChild.privateKey.toAddress().toString();
  const currentAddress = currChild.privateKey.toAddress().toString();
  return { rootPrivKey: rootChild.privateKey, currentPrivKey: currChild.privateKey, rootAddress, currentAddress, identityKey: computeBapId(rootAddress) };
}

// Build the BAP ID OP_RETURN (array of pushdata Buffers) rooted at `rootPrivKey`, AIP-signed by it.
// `currentPrivKey` is the current signing key (defaults to the root — a single-key identity, no
// rotation). This is the primitive; the HD and root-key helpers below just supply the keys.
function buildRecord(rootPrivKey, currentPrivKey) {
  const bsv = getBsv();
  const Buffer = Buf();
  const cur = currentPrivKey || rootPrivKey;
  const rootAddress = rootPrivKey.toAddress().toString();
  const currentAddress = cur.toAddress().toString();
  const identityKey = computeBapId(rootAddress);
  const pre = [Buffer.from(BAP_BITCOM_ADDRESS), Buffer.from('ID'), Buffer.from(identityKey), Buffer.from(currentAddress)];
  const sigParts = [Buffer.from([0x6a])];
  for (const b of pre) sigParts.push(b);
  sigParts.push(Buffer.from('|'));
  const aipMsg = Buffer.concat(sigParts);
  const sigBytes = Buffer.from(bsv.Message.sign(aipMsg, rootPrivKey), 'base64');
  const fields = pre.concat([Buffer.from('|'), Buffer.from(AIP_BITCOM_ADDRESS), Buffer.from('BITCOIN_ECDSA'), Buffer.from(rootAddress), sigBytes]);
  return { fields, rootAddress, currentAddress, identityKey, bapId: identityKey };
}

// Wallet-compatible HD identity (dedicated m/424150' seed).
function buildBapIdOpReturn(hdPrivKey, opts) {
  const { rootPrivKey, currentPrivKey } = deriveBapKeys(hdPrivKey, opts && opts.currentPath);
  return buildRecord(rootPrivKey, currentPrivKey);
}

// Verify the AIP signature on a built BAP ID record (array of Buffers).
function verifyBapIdSignature(fields) {
  const bsv = getBsv();
  const Buffer = Buf();
  if (!Array.isArray(fields) || fields.length < 9) return false;
  const pre = fields.slice(0, 4);
  if (Buffer.compare(pre[0], Buffer.from(BAP_BITCOM_ADDRESS)) !== 0) return false;
  if (Buffer.compare(pre[1], Buffer.from('ID')) !== 0) return false;
  const rootAddress = fields[7].toString('utf8');
  const sigBytes = fields[8];
  const sigParts = [Buffer.from([0x6a])];
  for (const b of pre) sigParts.push(b);
  sigParts.push(Buffer.from('|'));
  const aipMsg = Buffer.concat(sigParts);
  try { return bsv.Message.verify(aipMsg, rootAddress, sigBytes.toString('base64')); } catch (_) { return false; }
}

const toHexFields = (built) => ({ fieldsHex: built.fields.map((b) => b.toString('hex')), identityKey: built.identityKey, rootAddress: built.rootAddress, currentAddress: built.currentAddress });

// web3keys BAP identity: rooted at the user's identity key (the same key that controls the DID), so
// one key backs both did:web and the on-chain BAP identity. No mnemonic needed at publish time.
function buildFromRootKey(rootPrivKey) { return toHexFields(buildRecord(rootPrivKey)); }

// Wallet-compatible: build directly from a mnemonic (dedicated m/424150' HD identity).
function buildFromMnemonic(mnemonic) {
  const bsv = getBsv();
  const hd = bsv.Mnemonic.fromString(mnemonic).toHDPrivateKey('', bsv.Networks.mainnet);
  return toHexFields(buildBapIdOpReturn(hd));
}

const hexToFields = (hexArr) => { const Buffer = Buf(); return hexArr.map((h) => Buffer.from(String(h), 'hex')); };

module.exports = {
  BAP_ROOT_PATH, BAP_CURRENT_PATH, BAP_BITCOM_ADDRESS, AIP_BITCOM_ADDRESS,
  computeBapId, deriveBapKeys, buildRecord, buildBapIdOpReturn, verifyBapIdSignature, buildFromRootKey, buildFromMnemonic, hexToFields,
};
